// OpenSearch 검색 매핑 순수 함수 (OFM-212)
// 서비스/네트워크 의존이 없는 순수 변환 로직 — 단위 테스트 대상.
import type { OpenSearchHit } from 'services/openSearch'

// OpenSearch max_result_window. position + limit 가 이 값을 넘으면 빈/부분 응답.
export const MAX_RESULT_WINDOW = 10000

// 검색 결과에서는 emailId만 신뢰한다 (OFM-225). hit의 표시 필드(subject/from/preview)는
// 인덱스 유래라 keywords($seen/$flagged)·attachments가 없어, 그대로 이메일 데이터로 쓰면
// 캐시(emailDB)에 없는 메일이 "첨부 없음/안읽음" 기본값인 채 로드 완료로 오인된다.
// messageId를 비워 reqNextThreadList의 fake 경로(2단계 로딩)를 태우고, 실데이터는
// emailDB 캐시 또는 JMAP Email/get(배치)으로 로딩한다.
export interface AdaptedEmailData {
  id: string
  mailboxIds: Record<string, boolean>
  // 실데이터 로딩 전 정렬(receivedAt desc) 시드.
  receivedAt?: string
  // 검색 결과임을 표시. applyData가 이미 로드된 캐시 메일의 실데이터
  // (읽음/플래그/첨부/스레드/메일함 소속)를 덮어쓰지 않고, 미로드 메일은
  // id만으로 재조회하도록 하는 가드용.
  extra: { isOpenSearch: true }
}

// 메일함 → IMAP 경로명. inbox는 'INBOX', 하위는 'INBOX/<name>/...', 그 외는 raw name 경로.
// (표시명 getFullName/getDisplayName은 로케일 변환되므로 사용하지 않고 raw getName 사용)
export function toImapPath(mailbox: any, dataMgr: any): string {
  const segments: string[] = []
  let cur = mailbox
  let guard = 0
  while (cur != null && guard < 50) {
    guard += 1
    const role = typeof cur.getRole === 'function' ? cur.getRole() : null
    const id = cur.data?.id ?? (typeof cur.getId === 'function' ? cur.getId() : null)
    if (id === '__root__' || role === 'root') {
      break
    }
    if (role === 'inbox') {
      segments.unshift('INBOX')
      break
    }
    const name = typeof cur.getName === 'function' ? cur.getName() : cur.data?.name
    if (name == null || name === '') {
      return ''
    }
    segments.unshift(name)
    const parentId = cur.data?.parentId
    cur = parentId != null ? dataMgr.getMailboxById(parentId, true) : null
  }
  return segments.join('/')
}

// IMAP 경로 ↔ JMAP UUID 양방향 맵 (folderIds 생성 + mailboxIds 합성 공용)
export function buildPathMaps(dataMgr: any): {
  pathToUuid: Record<string, string>
  uuidToPath: Record<string, string>
} {
  const pathToUuid: Record<string, string> = {}
  const uuidToPath: Record<string, string> = {}
  const all: any[] = typeof dataMgr.getAllMailboxes === 'function' ? dataMgr.getAllMailboxes() : []
  all.forEach((mb) => {
    const uuid = typeof mb.getId === 'function' ? mb.getId() : null
    if (uuid == null) {
      return
    }
    const path = toImapPath(mb, dataMgr)
    if (path !== '') {
      pathToUuid[path] = uuid
      uuidToPath[uuid] = path
    }
  })
  return { pathToUuid, uuidToPath }
}

// 검색 대상 folderIds 계산.
// - 특정 메일함(innerMailbox): 해당 경로 1개 (JMAP inMailbox 동일, 하위 미포함)
// - 전체: trash/junk/drafts "루트"만 제외한 나머지 전체 경로 (JMAP inMailboxOtherThan 동일).
//   제외는 getMailboxForRole로 찾은 "루트" 메일함 id 기준이라, trash/junk/drafts의
//   "하위 폴더"는 별도 id를 가져 제외되지 않고 검색 대상에 포함된다(JMAP과 동일한 한계).
export function computeFolderIds(mailbox: any, dataMgr: any): string[] {
  const inner = typeof mailbox.getInnerMailbox === 'function' ? mailbox.getInnerMailbox() : null
  if (inner != null) {
    const path = toImapPath(inner, dataMgr)
    return path !== '' ? [path] : []
  }

  const excludeIds = new Set<string>()
  ;['trash', 'junk', 'drafts'].forEach((role) => {
    const mb = dataMgr.getMailboxForRole(role)
    if (mb != null && typeof mb.getId === 'function') {
      excludeIds.add(mb.getId())
    }
  })

  const folderIds: string[] = []
  const all: any[] = typeof dataMgr.getAllMailboxes === 'function' ? dataMgr.getAllMailboxes() : []
  all.forEach((mb) => {
    const id = typeof mb.getId === 'function' ? mb.getId() : null
    if (id == null || excludeIds.has(id)) {
      return
    }
    const path = toImapPath(mb, dataMgr)
    if (path !== '') {
      folderIds.push(path)
    }
  })
  return folderIds
}

// OpenSearch hit → emailId 중심 최소 데이터로 어댑팅 (OFM-225).
// emailId 없는 hit은 제외(null 반환). messageId를 비워 reqNextThreadList의 fake 경로를
// 태우고, 실데이터(keywords/attachments/subject 등)는 emailDB/JMAP에서 로딩한다.
export function adaptHit(
  hit: OpenSearchHit,
  pathToUuid: Record<string, string>,
): AdaptedEmailData | null {
  if (hit.emailId == null || hit.emailId === '') {
    console.warn('[openSearch] emailId 없는 hit 제외:', hit.mailboxId, hit.uid)
    return null
  }
  const mailboxIds: Record<string, boolean> = {}
  const uuid = pathToUuid[hit.mailboxId]
  if (uuid != null) {
    mailboxIds[uuid] = true
  }
  return {
    id: hit.emailId,
    mailboxIds,
    receivedAt: hit.receivedAt,
    extra: { isOpenSearch: true }, // 캐시 실데이터 보존/최소 재조회 가드 (applyData에서 소비)
  }
}

// OMD-21: JMAP Thread/get 결과 항목(스레드 id + 스레드 전체 emailIds).
export interface JmapThreadEntry {
  id: string
  emailIds: string[]
}

// OMD-21: JMAP Email/get(properties:['threadId']) 결과 항목.
export interface EmailThreadIdEntry {
  id: string
  threadId: string | null
}

export interface SearchThreadContract {
  emailsForThread: { id: string; threadId: string }[]
  allEmailList: AdaptedEmailData[]
}

// OMD-21: OpenSearch 매치 결과 + JMAP(threadId/Thread) 조회 결과로 THREAD 표시 계약을 만든다.
// - emailsForThread: 스레드당 대표(매치 순서=receivedAt desc 에서 threadId 첫 등장=최신 매치) 1건.
//   → reqNextThreadList가 threadIdMap을 만들어 스레드 titleEmail로 사용한다.
// - allEmailList: 스레드 전체 emailId(매치=어댑팅 항목 재사용해 mailboxIds/isOpenSearch 마커 보존,
//   비매치=최소 항목). messageId가 없어 reqNextThreadList의 fake 2단계 로딩으로 실데이터가 채워진다.
// emailIds(raw 매치)/total은 호출부가 그대로 유지한다(페이지네이션 Option A — emailId 기준).
export function buildSearchThreadContract(
  matchedEmailList: AdaptedEmailData[],
  emailThreadIdList: EmailThreadIdEntry[],
  threadList: JmapThreadEntry[],
): SearchThreadContract {
  const representativeSeen = new Set<string>()
  const emailsForThread: { id: string; threadId: string }[] = []
  emailThreadIdList.forEach((entry) => {
    if (entry == null || entry.threadId == null) {
      return
    }
    if (representativeSeen.has(entry.threadId) === true) {
      return
    }
    representativeSeen.add(entry.threadId)
    emailsForThread.push({ id: entry.id, threadId: entry.threadId })
  })

  const matchedById = new Map(matchedEmailList.map((data) => [data.id, data]))
  const includedEmailIds = new Set<string>()
  const allEmailList: AdaptedEmailData[] = []
  threadList.forEach((thread) => {
    if (thread == null || thread.emailIds == null) {
      return
    }
    thread.emailIds.forEach((emailId) => {
      if (includedEmailIds.has(emailId) === true) {
        return
      }
      includedEmailIds.add(emailId)
      const matched = matchedById.get(emailId)
      if (matched != null) {
        allEmailList.push(matched)
      } else {
        allEmailList.push({ id: emailId, mailboxIds: {}, extra: { isOpenSearch: true } })
      }
    })
  })

  return { emailsForThread, allEmailList }
}
